import { useSelector } from 'react-redux';

import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import { Typography } from '@components/styledComponents';
import { currencyFormatter } from '../../../payment-orchestration.utils';

import {
  selectSelectedOrchestrationRule,
  selectSelectedOrchestrationRuleLimits,
} from '../../../store/payment-orchestration-rules.slice';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default function CollectionStatus() {
  const selectedRule = useSelector(selectSelectedOrchestrationRule);
  const selectedRuleLimits = useSelector(selectSelectedOrchestrationRuleLimits);

  const remainingMonthlyLimit = selectedRuleLimits?.remainingMonthlyLimit || 0;
  const remainingMonthlyDisplay = `${
    selectedRule?.settlementCurrency
  } ${currencyFormatter({
    value: remainingMonthlyLimit,
    currency: selectedRule?.settlementCurrency,
  })}`;
  const now = dayjs().tz('Asia/Kuala_Lumpur');
  const currentMonth = now.format('MMMM YYYY');
  const startOfHour = now.startOf('hour');
  const endOfHour = now.endOf('hour');
  const currentHourRange = `${startOfHour.format(
    'h:mm A',
  )} - ${endOfHour.format('h:mm A')} (${now.format('zzz')})`;

  if (!selectedRuleLimits) return null;

  return (
    <>
      <Typography variant="formHeader">Collection Status</Typography>
      <Container
        disableGutters
        sx={{ m: 0, p: 0 }}
        maxWidth="sm">
        <TableContainer component="div">
          <Table>
            <TableHead>
              <TableRow style={{ verticalAlign: 'bottom' }}>
                <TableCell align="left">Month</TableCell>
                <TableCell align="right">Monthy Cap</TableCell>
                <TableCell align="right">
                  Remaining Commission <br />
                  To Be Collected For the Month
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{currentMonth}</TableCell>
                <TableCell align="right">
                  {selectedRule.settlementCurrency}{' '}
                  {currencyFormatter({
                    value: selectedRule.monthlyCap,
                    currency: selectedRule.settlementCurrency,
                  })}
                </TableCell>
                <TableCell align="right">{remainingMonthlyDisplay}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
      <Container
        disableGutters
        sx={{ m: 0, p: 0, marginTop: 5 }}
        maxWidth="md">
        <TableContainer component="div">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={5}>
                  Time Range: {currentHourRange}
                </TableCell>
              </TableRow>
              <TableRow style={{ verticalAlign: 'bottom' }}>
                <TableCell>Payment Currency</TableCell>
                <TableCell>Weightage</TableCell>
                {selectedRuleLimits?.spillover && (
                  <TableCell align="right">
                    Collection Spillover <br /> (Cumulative)
                  </TableCell>
                )}
                {selectedRuleLimits?.remainingHourlyLimit && (
                  <TableCell align="right">
                    Remaining Commission <br /> To Be Collected <br />
                    (Up-to-date)
                  </TableCell>
                )}
                {selectedRuleLimits?.commissionCollected && (
                  <TableCell align="right">
                    Collected Commission <br />
                    (Up-to-date)
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(selectedRule.currencyWeightage).map(
                ([currency, percentage]) => {
                  const remainingHourly =
                    selectedRuleLimits?.remainingHourlyLimit?.[currency];
                  const commissionCollected =
                    selectedRuleLimits?.commissionCollected?.[currency];
                  const spillover = selectedRuleLimits?.spillover?.[currency];

                  return (
                    <TableRow key={currency}>
                      <TableCell>{currency}</TableCell>
                      <TableCell align="center">{percentage}%</TableCell>
                      {selectedRuleLimits?.spillover && (
                        <TableCell align="right">
                          {selectedRule.settlementCurrency}{' '}
                          {currencyFormatter({
                            value: spillover || 0,
                            currency: selectedRule.settlementCurrency,
                          })}
                        </TableCell>
                      )}
                      {selectedRuleLimits?.remainingHourlyLimit && (
                        <TableCell align="right">
                          {selectedRule.settlementCurrency}{' '}
                          {currencyFormatter({
                            value: remainingHourly || 0,
                            currency: selectedRule.settlementCurrency,
                          })}
                        </TableCell>
                      )}
                      {selectedRuleLimits?.commissionCollected && (
                        <TableCell align="right">
                          {selectedRule.settlementCurrency}{' '}
                          {currencyFormatter({
                            value: commissionCollected || 0,
                            currency: selectedRule.settlementCurrency,
                          })}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                },
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  );
}
